import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { de, enUS } from "date-fns/locale";
import {
  Users,
  CalendarDays,
  Crown,
  TrendingUp,
  Euro,
  Check,
  Loader2,
  Bell,
  BarChart3,
  Mail,
  MessageSquare,
  Trash2,
  Edit,
  X,
  Save,
  Clock,
  Ban,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Verfügbare Zeitslots
const TIME_SLOTS = [
  "08:00", "08:30", "09:00", "09:30", "10:00", "10:30",
  "11:00", "11:30", "12:00", "12:30", "13:00", "13:30",
  "14:00", "14:30", "15:00", "15:30", "16:00", "16:30",
  "17:00", "17:30"
];

export default function AdminPage() {
  const navigate = useNavigate();
  const { isAuthenticated, token, user, loading: authLoading } = useAuth();
  const { t, language } = useLanguage();

  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("stats");
  const [statusFilter, setStatusFilter] = useState("all");
  const [updatingBooking, setUpdatingBooking] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserData, setEditUserData] = useState({});
  
  // Buchung bearbeiten State
  const [editingBooking, setEditingBooking] = useState(null);
  const [editBookingData, setEditBookingData] = useState({
    date: "",
    time_slot: "",
    status: ""
  });

  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated) {
        navigate("/login?redirect=/admin");
      } else if (user?.role !== "admin") {
        toast.error(language === "de" ? "Admin-Zugang erforderlich" : "Admin access required");
        navigate("/dashboard");
      }
    }
  }, [authLoading, isAuthenticated, user, navigate, language]);

  useEffect(() => {
    if (isAuthenticated && token && user?.role === "admin") {
      fetchData();
    }
  }, [isAuthenticated, token, user]);

  const fetchData = async () => {
    try {
      const [statsRes, bookingsRes, usersRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/notifications`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setUsers(usersRes.data);
      setNotifications(notificationsRes.data);
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  // Buchung bearbeiten starten
  const handleEditBooking = (booking) => {
    setEditingBooking(booking.id);
    setEditBookingData({
      date: booking.date.split('T')[0],
      time_slot: booking.time_slot,
      status: booking.status
    });
  };

  // Buchung speichern
  const handleSaveBooking = async (bookingId) => {
    setUpdatingBooking(bookingId);
    try {
      await axios.put(
        `${API}/admin/bookings/${bookingId}`,
        editBookingData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(language === "de" ? "Buchung aktualisiert" : "Booking updated");
      setEditingBooking(null);
      fetchData();
    } catch (error) {
      toast.error(language === "de" ? "Fehler beim Aktualisieren" : "Failed to update booking");
    } finally {
      setUpdatingBooking(null);
    }
  };

  // Buchung stornieren
  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm(language === "de" ? "Buchung wirklich stornieren?" : "Really cancel booking?")) {
      return;
    }
    setUpdatingBooking(bookingId);
    try {
      await axios.put(
        `${API}/admin/bookings/${bookingId}`,
        { status: "cancelled" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(language === "de" ? "Buchung storniert" : "Booking cancelled");
      fetchData();
    } catch (error) {
      toast.error(language === "de" ? "Fehler beim Stornieren" : "Failed to cancel booking");
    } finally {
      setUpdatingBooking(null);
    }
  };

  const handleUpdateBooking = async (bookingId, status) => {
    setUpdatingBooking(bookingId);
    try {
      await axios.put(
        `${API}/admin/bookings/${bookingId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(language === "de" ? "Buchung aktualisiert" : "Booking updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update booking");
    } finally {
      setUpdatingBooking(null);
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm(language === "de" ? "Buchung wirklich löschen?" : "Really delete booking?")) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === "de" ? "Buchung gelöscht" : "Booking deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete booking");
    }
  };

  const handleEditUser = (u) => {
    setEditingUser(u.id);
    setEditUserData({
      membership_type: u.membership_type,
      role: u.role,
      loyalty_points: u.loyalty_points || 0
    });
  };

  const handleSaveUser = async (userId) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}`,
        editUserData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(language === "de" ? "Benutzer aktualisiert" : "User updated");
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(language === "de" ? "Benutzer und alle Buchungen wirklich löschen?" : "Really delete user and all bookings?")) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === "de" ? "Benutzer gelöscht" : "User deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const filteredBookings = statusFilter === "all" 
    ? bookings 
    : bookings.filter(b => b.status === statusFilter);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (user?.role !== "admin") {
    return null;
  }

  const locale = language === "de" ? de : enUS;

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="admin-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-2 font-['Chivo']">
            {t("admin_title")}
          </h1>
          <p className="text-zinc-400">
            {language === "de" ? "Verwalten Sie Buchungen, Benutzer und mehr." : "Manage bookings, users, and more."}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { id: "stats", label: t("admin_stats"), icon: BarChart3 },
            { id: "bookings", label: t("admin_bookings"), icon: CalendarDays },
            { id: "users", label: t("admin_users"), icon: Users },
            { id: "notifications", label: t("admin_notifications"), icon: Bell },
          ].map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "outline"}
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id 
                ? "bg-cyan-500 text-black hover:bg-cyan-400" 
                : "border-zinc-700 text-zinc-400 hover:text-white"}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Stats Tab */}
        {activeTab === "stats" && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6" data-testid="stat-users">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-cyan-500/10 rounded-sm flex items-center justify-center">
                    <Users className="h-6 w-6 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">{t("admin_total_users")}</p>
                    <p className="text-2xl font-bold text-white">{stats.total_users}</p>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6" data-testid="stat-premium">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-sm flex items-center justify-center">
                    <Crown className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">{t("admin_premium_users")}</p>
                    <p className="text-2xl font-bold text-white">{stats.premium_users}</p>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6" data-testid="stat-bookings">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500/10 rounded-sm flex items-center justify-center">
                    <CalendarDays className="h-6 w-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">{t("admin_total_bookings")}</p>
                    <p className="text-2xl font-bold text-white">{stats.total_bookings}</p>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6" data-testid="stat-revenue">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-500/10 rounded-sm flex items-center justify-center">
                    <Euro className="h-6 w-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">{t("admin_revenue")}</p>
                    <p className="text-2xl font-bold text-white">{stats.total_revenue.toFixed(2)}€</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bookings by Service */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-cyan-400" />
                {language === "de" ? "Buchungen nach Service" : "Bookings by Service"}
              </h3>
              <div className="space-y-3">
                {stats.bookings_by_service.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-zinc-300">{item._id}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 h-2 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-cyan-500 rounded-full"
                          style={{ width: `${(item.count / stats.total_bookings) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm text-zinc-500 w-12 text-right">{item.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 mb-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48 bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all" className="text-white">{t("common_all")}</SelectItem>
                  <SelectItem value="pending" className="text-white">{language === "de" ? "Ausstehend" : "Pending"}</SelectItem>
                  <SelectItem value="confirmed" className="text-white">{t("common_confirmed")}</SelectItem>
                  <SelectItem value="completed" className="text-white">{t("common_completed")}</SelectItem>
                  <SelectItem value="cancelled" className="text-white">{t("common_cancelled")}</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-zinc-500">
                {filteredBookings.length} {language === "de" ? "Buchungen" : "bookings"}
              </span>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-800/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400">{t("common_date")}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400">{t("common_time")}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400">Service</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400">{language === "de" ? "Kunde" : "Customer"}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400">{t("common_status")}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400">{language === "de" ? "Aktionen" : "Actions"}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {filteredBookings.map((booking) => (
                      <tr key={booking.id} className="hover:bg-zinc-800/30" data-testid={`admin-booking-${booking.id}`}>
                        {/* Datum */}
                        <td className="px-4 py-3 text-sm text-white">
                          {editingBooking === booking.id ? (
                            <input
                              type="date"
                              value={editBookingData.date}
                              onChange={(e) => setEditBookingData({...editBookingData, date: e.target.value})}
                              className="w-36 h-8 px-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                            />
                          ) : (
                            format(new Date(booking.date), "dd.MM.yyyy")
                          )}
                        </td>
                        
                        {/* Uhrzeit */}
                        <td className="px-4 py-3 text-sm text-white">
                          {editingBooking === booking.id ? (
                            <Select 
                              value={editBookingData.time_slot} 
                              onValueChange={(v) => setEditBookingData({...editBookingData, time_slot: v})}
                            >
                              <SelectTrigger className="w-24 h-8 bg-zinc-800 border-zinc-700 text-white text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-800 border-zinc-700 max-h-48">
                                {TIME_SLOTS.map((slot) => (
                                  <SelectItem key={slot} value={slot} className="text-white">{slot}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            booking.time_slot
                          )}
                        </td>
                        
                        <td className="px-4 py-3 text-sm text-white">{booking.service_name}</td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-white">{booking.user_name}</p>
                          <p className="text-xs text-zinc-500">{booking.user_email}</p>
                        </td>
                        
                        {/* Status */}
                        <td className="px-4 py-3">
                          {editingBooking === booking.id ? (
                            <Select 
                              value={editBookingData.status} 
                              onValueChange={(v) => setEditBookingData({...editBookingData, status: v})}
                            >
                              <SelectTrigger className="w-32 h-8 bg-zinc-800 border-zinc-700 text-white text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-800 border-zinc-700">
                                <SelectItem value="pending" className="text-white">{language === "de" ? "Ausstehend" : "Pending"}</SelectItem>
                                <SelectItem value="confirmed" className="text-white">{language === "de" ? "Bestätigt" : "Confirmed"}</SelectItem>
                                <SelectItem value="completed" className="text-white">{language === "de" ? "Abgeschlossen" : "Completed"}</SelectItem>
                                <SelectItem value="cancelled" className="text-white">{language === "de" ? "Storniert" : "Cancelled"}</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className={`px-2 py-1 text-xs rounded-sm ${
                              booking.status === "pending" ? "bg-amber-500/10 text-amber-400" :
                              booking.status === "confirmed" ? "bg-blue-500/10 text-blue-400" :
                              booking.status === "completed" ? "bg-green-500/10 text-green-400" :
                              "bg-red-500/10 text-red-400"
                            }`}>
                              {booking.status === "pending" ? (language === "de" ? "Ausstehend" : "Pending") :
                               booking.status === "confirmed" ? t("common_confirmed") :
                               booking.status === "completed" ? t("common_completed") :
                               t("common_cancelled")}
                            </span>
                          )}
                        </td>
                        
                        {/* Aktionen */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {editingBooking === booking.id ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => handleSaveBooking(booking.id)}
                                  disabled={updatingBooking === booking.id}
                                  className="bg-green-600 hover:bg-green-500 text-white"
                                >
                                  {updatingBooking === booking.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Save className="h-3 w-3" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingBooking(null)}
                                  className="border-zinc-600"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </>
                            ) : (
                              <>
                                {/* Bestätigen */}
                                {booking.status === "pending" && (
                                  <Button
                                    size="sm"
                                    onClick={() => handleUpdateBooking(booking.id, "confirmed")}
                                    disabled={updatingBooking === booking.id}
                                    className="bg-blue-600 hover:bg-blue-500 text-white"
                                    title={language === "de" ? "Bestätigen" : "Confirm"}
                                  >
                                    {updatingBooking === booking.id ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Check className="h-3 w-3" />
                                    )}
                                  </Button>
                                )}
                                
                                {/* Abschließen */}
                                {booking.status === "confirmed" && (
                                  <Button
                                    size="sm"
                                    onClick={() => handleUpdateBooking(booking.id, "completed")}
                                    disabled={updatingBooking === booking.id}
                                    className="bg-green-600 hover:bg-green-500 text-white"
                                    title={language === "de" ? "Abschließen" : "Complete"}
                                  >
                                    {updatingBooking === booking.id ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Check className="h-3 w-3" />
                                    )}
                                  </Button>
                                )}
                                
                                {/* Bearbeiten */}
                                {booking.status !== "cancelled" && booking.status !== "completed" && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleEditBooking(booking)}
                                    className="border-zinc-600 text-zinc-400 hover:text-white"
                                    title={language === "de" ? "Bearbeiten" : "Edit"}
                                  >
                                    <Edit className="h-3 w-3" />
                                  </Button>
                                )}
                                
                                {/* Stornieren */}
                                {booking.status !== "cancelled" && booking.status !== "completed" && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleCancelBooking(booking.id)}
                                    disabled={updatingBooking === booking.id}
                                    className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                                    title={language === "de" ? "Stornieren" : "Cancel"}
                                  >
                                    <Ban className="h-3 w-3" />
                                  </Button>
                                )}
                                
                                {/* Löschen */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteBooking(booking.id)}
                                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                  title={language === "de" ? "Löschen" : "Delete"}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-800/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400">{language === "de" ? "Mitgliedschaft" : "Membership"}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400">Rolle</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400">{language === "de" ? "Punkte" : "Points"}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400">{language === "de" ? "Aktionen" : "Actions"}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-zinc-800/30" data-testid={`admin-user-${u.id}`}>
                      <td className="px-4 py-3 text-sm text-white">{u.name}</td>
                      <td className="px-4 py-3 text-sm text-zinc-400">{u.email}</td>
                      <td className="px-4 py-3">
                        {editingUser === u.id ? (
                          <Select 
                            value={editUserData.membership_type} 
                            onValueChange={(v) => setEditUserData({...editUserData, membership_type: v})}
                          >
                            <SelectTrigger className="w-28 h-8 bg-zinc-800 border-zinc-700 text-white text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              <SelectItem value="free" className="text-white">Free</SelectItem>
                              <SelectItem value="premium" className="text-white">Premium</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`px-2 py-1 text-xs rounded-sm ${
                            u.membership_type === "premium" ? "bg-amber-500/10 text-amber-400" : "bg-zinc-700 text-zinc-400"
                          }`}>
                            {u.membership_type === "premium" ? "Premium" : "Free"}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {editingUser === u.id ? (
                          <Select 
                            value={editUserData.role} 
                            onValueChange={(v) => setEditUserData({...editUserData, role: v})}
                          >
                            <SelectTrigger className="w-24 h-8 bg-zinc-800 border-zinc-700 text-white text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              <SelectItem value="user" className="text-white">User</SelectItem>
                              <SelectItem value="admin" className="text-white">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`px-2 py-1 text-xs rounded-sm ${
                            u.role === "admin" ? "bg-cyan-500/10 text-cyan-400" : "bg-zinc-700 text-zinc-400"
                          }`}>
                            {u.role === "admin" ? "Admin" : "User"}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {editingUser === u.id ? (
                          <input
                            type="number"
                            value={editUserData.loyalty_points}
                            onChange={(e) => setEditUserData({...editUserData, loyalty_points: parseInt(e.target.value) || 0})}
                            className="w-20 h-8 px-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                          />
                        ) : (
                          <span className="text-sm text-white">{u.loyalty_points || 0}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {editingUser === u.id ? (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleSaveUser(u.id)}
                                className="bg-green-600 hover:bg-green-500 text-white"
                              >
                                <Save className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingUser(null)}
                                className="border-zinc-600"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditUser(u)}
                                className="border-zinc-600 text-zinc-400 hover:text-white"
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteUser(u.id)}
                                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                disabled={u.id === user?.id}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === "notifications" && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
            <div className="mb-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm">
              <p className="text-sm text-amber-200">
                <strong>HINWEIS:</strong> E-Mail und SMS sind derzeit im Mock-Modus. 
                Für echte Benachrichtigungen SMTP (Strato) und SMS (Telekom) konfigurieren.
              </p>
            </div>
            
            <div className="space-y-3">
              {notifications.length === 0 ? (
                <p className="text-zinc-500 text-center py-8">
                  {language === "de" ? "Keine Benachrichtigungen" : "No notifications"}
                </p>
              ) : (
                notifications.map((notif, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-zinc-800/50 rounded-sm">
                    <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${
                      notif.type === "email" ? "bg-blue-500/10" : "bg-green-500/10"
                    }`}>
                      {notif.type === "email" ? (
                        <Mail className="h-4 w-4 text-blue-400" />
                      ) : (
                        <MessageSquare className="h-4 w-4 text-green-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-white">{notif.subject}</span>
                        <span className={`px-1.5 py-0.5 text-xs rounded ${
                          notif.status === "mocked" ? "bg-amber-500/10 text-amber-400" : "bg-green-500/10 text-green-400"
                        }`}>
                          {notif.status}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-500">{notif.recipient}</p>
                      <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{notif.message}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
